import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MessageCircle, ChevronRight, BookOpen, Mic, Ear, Heart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMMUNICATION_CONTENT = [
  {
    id: 'basics',
    title: 'Grunderna i god kommunikation',
    icon: MessageCircle,
    description: 'Lär dig uttrycka dig tydligt och lyssna aktivt.',
    content: [
      '**Jag-budskap:** Säg "Jag känner mig..." istället för "Du gör alltid..."',
      '**Aktivt lyssnande:** Lyssna för att förstå, inte för att svara.',
      '**Validering:** Bekräfta den andras känslor även om du inte håller med.',
      '**Pausa:** Ta en paus om känslorna blir för starka.',
      '**Timing:** Välj rätt tidpunkt för viktiga samtal – inte när någon är stressad eller trött.',
      '70% av kommunikation är icke-verbal – var medveten om ditt kroppsspråk.'
    ],
    questions: [
      'Hur kommunicerar du när du är arg eller sårad?',
      'Känner du dig hörd i dina relationer?',
      'Vad skulle du vilja säga som du inte vågar?',
      'Avbryter du ofta eller väntar du tills den andre pratat klart?'
    ]
  },
  {
    id: 'difficult',
    title: 'Svåra samtal',
    icon: Mic,
    description: 'Verktyg för att ta upp känsliga ämnen konstruktivt.',
    content: [
      '**Förbered dig:** Skriv ner vad du vill säga innan samtalet.',
      '**Välj tid och plats:** Be om ett samtal när ni båda är lugna.',
      '**Börja mjukt:** "Jag skulle vilja prata om något viktigt för mig..."',
      '**Beskriv, döm inte:** Säg vad du observerar utan att tolka eller anklaga.',
      '**Lyssna på svaret:** Låt den andre svara utan att avbryta.',
      '**Sök lösning:** Fokusera på hur ni kan gå vidare tillsammans.',
      '**Acceptera att det tar tid:** Stora frågor löses sällan i ett samtal.'
    ],
    questions: [
      'Vilket samtal har du skjutit upp?',
      'Vad är du rädd ska hända om du tar upp det?',
      'Hur brukar du reagera när din partner tar upp något svårt?',
      'Kan du lyssna utan att försvara dig?'
    ]
  },
  {
    id: 'nvc',
    title: 'Icke-våldsam kommunikation (IVK)',
    icon: Heart,
    description: 'En beprövad metod för respektfull dialog.',
    content: [
      '**IVK har fyra steg:**',
      '**1. Observation:** Beskriv vad du ser utan värdering. "När du kommer hem sent utan att meddela..."',
      '**2. Känsla:** Uttryck hur du känner dig. "...känner jag mig orolig och oviktig."',
      '**3. Behov:** Förklara vilket behov som inte är tillgodosett. "Jag behöver känna mig prioriterad."',
      '**4. Önskan:** Formulera en tydlig, genomförbar önskan. "Kan du meddela om du blir sen?"',
      'IVK hjälper dig uttrycka dig utan att den andre går i försvar.'
    ],
    questions: [
      'Kan du formulera ett aktuellt problem med IVK-modellen?',
      'Vilka ord använder du som kan uppfattas som anklagelser?',
      'Vad är ditt underliggande behov i en pågående konflikt?'
    ]
  },
  {
    id: 'listening',
    title: 'Aktivt lyssnande',
    icon: Ear,
    description: 'Konsten att verkligen höra vad någon säger.',
    content: [
      '**Ge din fulla uppmärksamhet:** Lägg bort telefonen och titta på personen.',
      '**Visa att du lyssnar:** Nicka, humma, ha ögonkontakt.',
      '**Avbryt inte:** Låt personen prata klart innan du svarar.',
      '**Reflektera tillbaka:** "Om jag förstår dig rätt så menar du..."',
      '**Ställ öppna frågor:** "Hur kändes det?" istället för "Var du arg?"',
      '**Undvik att ge råd direkt:** Ibland vill folk bara bli hörda.',
      '**Sammanfatta:** "Det låter som att det viktigaste för dig är..."'
    ],
    questions: [
      'Hur ofta lyssnar du verkligen utan att tänka på vad du ska säga?',
      'Brukar du ge råd även när ingen bett om det?',
      'Känner människor sig hörda av dig?'
    ]
  },
  {
    id: 'express',
    title: 'Uttrycka känslor',
    icon: MessageCircle,
    description: 'Hur du sätter ord på det du känner.',
    content: [
      '**Bygg känslospråk:** Lär dig fler ord än "arg" och "ledsen".',
      '**Känn efter i kroppen:** Var känner du känslan? Mage, bröst, hals?',
      '**Namnge känslan:** "Just nu känner jag mig..."',
      '**Var specifik:** "Jag känner mig övergiven" är tydligare än "jag mår dåligt".',
      '**Ta ansvar:** "Jag känner mig frustrerad" istället för "du gör mig frustrerad".',
      '**Tillåt alla känslor:** Det finns inga "dåliga" känslor – bara information.'
    ],
    questions: [
      'Vilka känslor har du svårt att uttrycka?',
      'Hur lärde du dig hantera känslor som barn?',
      'Finns det känslor du skäms för?'
    ]
  }
];

export default function CommunicationPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [selectedTopic, setSelectedTopic] = useState(null);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="communication-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Kommunikation</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till kommunikationsverktyg.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  if (selectedTopic) {
    const topic = COMMUNICATION_CONTENT.find(t => t.id === selectedTopic);
    return (
      <div className="min-h-screen bg-background" data-testid="communication-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedTopic(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Kommunikation</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-blue-50 rounded-2xl p-8">
            <topic.icon className="w-10 h-10 text-blue-600 mb-4" />
            <h2 className="font-heading text-2xl font-semibold mb-2">{topic.title}</h2>
            <p className="text-muted-foreground">{topic.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            {topic.content.map((text, i) => (
              <p key={i} className="text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            ))}
          </div>

          <div className="bg-primary/5 rounded-2xl p-6 space-y-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Reflektionsfrågor
            </h3>
            {topic.questions.map((q, i) => (
              <div key={i} className="flex gap-3 items-start p-4 rounded-xl bg-white">
                <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">{i + 1}</span>
                <span>{q}</span>
              </div>
            ))}
          </div>

          {/* Contact */}
          
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="communication-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Kommunikation</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-3">
          <MessageCircle className="w-12 h-12 mx-auto text-blue-500" />
          <h2 className="font-heading text-2xl font-semibold">Kommunikation</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Lär dig uttrycka dig tydligt, lyssna aktivt och föra konstruktiva samtal.
          </p>
        </div>

        <div className="space-y-3">
          {COMMUNICATION_CONTENT.map((topic) => (
            <button
              key={topic.id}
              onClick={() => setSelectedTopic(topic.id)}
              className="w-full bg-white p-5 rounded-2xl border border-border/30 text-left hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
                  <topic.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{topic.title}</h3>
                  <p className="text-sm text-muted-foreground">{topic.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
              </div>
            </button>
          ))}
        </div>

        {/* Quick tip */}
        <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
          <p className="text-blue-800 text-sm">
            💡 <strong>Snabbtips:</strong> Nästa gång du vill säga "Du gör alltid...", prova istället "Jag känner mig... när..."
          </p>
        </div>

        {/* Contact */}
      </main>
    </div>
  );
}
